import { useEffect, useCallback } from 'react';
import type { SupportedLanguage } from '@/lib/config/i18n';

interface UseLanguageOptions {
  fallbackLanguage?: SupportedLanguage;
  i18nInstance?: any;
  enableBodyClasses?: boolean;
  enableDocumentAttributes?: boolean;
}

interface UseLanguageReturn {
  changeLanguage: (language: string) => void;
  currentLanguage: SupportedLanguage;
}

export const useLanguage = (
  lang: string,
  options: UseLanguageOptions = {}
): UseLanguageReturn => {
  const {
    fallbackLanguage = 'ar',
    i18nInstance,
    enableBodyClasses = true,
    enableDocumentAttributes = true,
  } = options;

  const getSupportedLanguage = useCallback((language: string): SupportedLanguage => {
    return language === 'en' || language === 'ar'
      ? (language as SupportedLanguage)
      : fallbackLanguage;
  }, [fallbackLanguage]);

  const updateDocumentDirection = useCallback((language: SupportedLanguage) => {
    if (!enableDocumentAttributes) return;

    const direction = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
  }, [enableDocumentAttributes]);

  const updateBodyClasses = useCallback((language: SupportedLanguage) => {
    if (!enableBodyClasses) return;

    const isRtl = language === 'ar';
    document.body.classList.toggle('rtl', isRtl);
    document.body.classList.toggle('ltr', !isRtl);
  }, [enableBodyClasses]);

  const changeLanguage = useCallback((language: string) => {
    const supportedLang = getSupportedLanguage(language);

    if (i18nInstance?.changeLanguage) {
      i18nInstance.changeLanguage(supportedLang);
    }

    updateDocumentDirection(supportedLang);
    updateBodyClasses(supportedLang);
  }, [getSupportedLanguage, i18nInstance, updateDocumentDirection, updateBodyClasses]);

  useEffect(() => {
    changeLanguage(lang);
  }, [lang, changeLanguage]);

  return {
    changeLanguage,
    currentLanguage: getSupportedLanguage(lang),
  };
};
